import React, { useState, useRef, useCallback, useEffect } from "react";
import { API_URL } from "../config";
import "./Studio.css";

function getToken() {
  return localStorage.getItem("token") || "";
}

function ImageModal({ src, label, onClose }) {
  if (!src) return null;
  return (
    <div className="st-modal-overlay" onClick={onClose}>
      <div className="st-modal" onClick={(e) => e.stopPropagation()}>
        <button className="st-modal__close" onClick={onClose}>&times;</button>
        <img src={src} alt={label} className="st-modal__image" />
        <span className="st-modal__label">{label}</span>
      </div>
    </div>
  );
}

const resizeImage = (file, maxDim = 2048) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      if (width <= maxDim && height <= maxDim && file.size < 3_500_000) { resolve(file); return; }
      const scale = Math.min(maxDim / width, maxDim / height, 1);
      width = Math.round(width * scale);
      height = Math.round(height * scale);
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      canvas.toBlob((blob) => resolve(blob), "image/png");
    };
    img.src = URL.createObjectURL(file);
  });

const CATEGORIES = [
  { id: "garments", label: "Ropa" },
  { id: "accessories", label: "Complementos" },
  { id: "artifacts", label: "Artefactos" },
];

const MODELS = [
  { id: "sdxl", label: "SDXL (Juggernaut)" },
  { id: "flux2", label: "Flux2.Dev" },
];

const RATIOS = ["1:1", "3:4", "4:3"];
const RESOLUTIONS = ["512", "768", "1024"];

export default function StudioDetails() {
  /* --- Category --- */
  const [category, setCategory] = useState("garments");

  /* --- Asset lists per category --- */
  const [items, setItems] = useState({ garments: [], accessories: [], artifacts: [] });
  const [selectedItem, setSelectedItem] = useState("");

  /* --- Reference image --- */
  const [refImage, setRefImage] = useState(null);
  const refInputRef = useRef(null);

  /* --- Prompts --- */
  const [idea, setIdea] = useState("");
  const [prompter, setPrompter] = useState("");

  /* --- Settings --- */
  const [model, setModel] = useState("sdxl");
  const [ratio, setRatio] = useState("1:1");
  const [resolution, setResolution] = useState("1024");
  const [seed, setSeed] = useState("");

  /* --- Generation --- */
  const [status, setStatus] = useState("idle");
  const [result, setResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [modalSrc, setModalSrc] = useState(null);

  /* --- Reel refs --- */
  const itemsReelRef = useRef(null);
  const scrollReel = (ref, dir) => {
    if (ref.current) ref.current.scrollBy({ left: dir * 200, behavior: "smooth" });
  };

  /* --- Load wardrobe on mount --- */
  const loadItems = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/wardrobe`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = await res.json();
      setItems({
        garments: data.garments || [],
        accessories: data.accessories || [],
        artifacts: [], // TODO: artifacts endpoint
      });
    } catch {}
  }, []);

  useEffect(() => { loadItems(); }, [loadItems]);

  const currentItems = items[category] || [];

  /* --- File handler --- */
  const handleRefUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const resized = await resizeImage(file);
    setRefImage(URL.createObjectURL(resized));
    if (refInputRef.current) refInputRef.current.value = "";
  };

  /* --- Convert blob to b64 --- */
  const blobToB64 = async (blobUrl) => {
    const resp = await fetch(blobUrl);
    const blob = await resp.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result.split(",")[1]);
      reader.readAsDataURL(blob);
    });
  };

  const pollRef = useRef(false);
  const pollJob = async (jobId) => {
    pollRef.current = true;
    for (let i = 0; i < 120; i++) {
      if (!pollRef.current) return;
      await new Promise((r) => setTimeout(r, 3000));
      try {
        const res = await fetch(`${API_URL}/studio/details/${encodeURIComponent(jobId)}/status`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        const data = await res.json();
        if (data.status === "completed") {
          const rr = await fetch(`${API_URL}/studio/details/${encodeURIComponent(jobId)}/result`, {
            headers: { Authorization: `Bearer ${getToken()}` },
          });
          const rd = await rr.json();
          if (rd.image) setResult(rd.image);
          setStatus("done");
          pollRef.current = false;
          return;
        }
        if (data.status === "error") {
          setErrorMsg(data.error || "Error al generar");
          setStatus("error");
          pollRef.current = false;
          return;
        }
      } catch (err) { console.error("Poll error:", err); }
    }
    setErrorMsg("Tiempo agotado");
    setStatus("error");
    pollRef.current = false;
  };

  /* --- Generate --- */
  const canGenerate = (refImage || idea.trim()) && status !== "generating";

  const handleGenerate = async () => {
    setStatus("generating");
    setResult(null);
    setErrorMsg("");
    try {
      const body = {
        category, idea, prompter, model, ratio, resolution, seed,
        ref_image_b64: refImage ? await blobToB64(refImage) : "",
      };
      const res = await fetch(`${API_URL}/studio/details/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Error ${res.status}`);
      }
      const data = await res.json();
      pollJob(data.job_id);
    } catch (err) {
      setErrorMsg(err.message || "Error al generar");
      setStatus("error");
    }
  };

  const getResultSrc = () => {
    if (!result) return null;
    return result.startsWith("data:") ? result : `data:image/png;base64,${result}`;
  };

  return (
    <div className="studio-page">
      {/* LEFT */}
      <div className="st-panel st-panel--left">
        <div className="st-header">
          <h2 className="st-panel__title">Crear Detalles</h2>
          <button
            className={`st-btn-primary ${status === "generating" ? "st-btn-primary--loading" : ""}`}
            disabled={!canGenerate}
            onClick={handleGenerate}
          >
            {status === "generating" ? "Generando..." : "Generar"}
          </button>
        </div>

        {/* Category selector */}
        <section className="st-section">
          <span className="st-section__title">Categoria</span>
          <div style={{ display: "flex", gap: 6 }}>
            {CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                className="st-btn-secondary"
                style={category === cat.id ? { background: "var(--turquesa)", color: "var(--dark)" } : {}}
                onClick={() => { setCategory(cat.id); setSelectedItem(""); }}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </section>

        {/* Items reel */}
        <section className="st-section">
          <span className="st-section__title">{CATEGORIES.find((c) => c.id === category)?.label || "Items"} existentes</span>
          <div className="st-reel st-reel--tall">
            <button className="st-reel__btn" onClick={() => scrollReel(itemsReelRef, -1)}>{"\u25C0"}</button>
            <div className="st-reel__frames" ref={itemsReelRef}>
              <div className="st-reel-item st-reel-item--add st-reel-item--wide" onClick={() => setSelectedItem("")}>+</div>
              {currentItems.map((item) => (
                <div
                  key={item.url || item.name}
                  className={`st-reel-item st-reel-item--wide ${selectedItem === item.url ? "st-reel-item--selected" : ""}`}
                  onClick={() => setSelectedItem(item.url)}
                >
                  <img src={item.url} alt={item.name} />
                  <span className="st-reel-item__name">{item.name?.replace(".png", "").replace(/_/g, " ")}</span>
                </div>
              ))}
              {currentItems.length === 0 && <span className="st-reel-empty">Sin {category}</span>}
            </div>
            <button className="st-reel__btn" onClick={() => scrollReel(itemsReelRef, 1)}>{"\u25B6"}</button>
          </div>
        </section>

        {/* Reference image */}
        <section className="st-section">
          <span className="st-section__title">Imagen de referencia</span>
          <div
            className="st-upload-zone"
            style={refImage ? { minHeight: 140 } : {}}
            onClick={() => !refImage && refInputRef.current?.click()}
          >
            {refImage ? (
              <>
                <img src={refImage} alt="Ref" />
                <button className="st-upload-zone__remove" onClick={(e) => { e.stopPropagation(); setRefImage(null); }}>&times;</button>
              </>
            ) : (
              <span>Haz clic para cargar imagen de referencia</span>
            )}
          </div>
          <input ref={refInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleRefUpload} />
        </section>

        {/* Prompts */}
        <section className="st-section">
          <span className="st-section__title">Idea</span>
          <textarea
            className="st-textarea"
            placeholder="Describe el detalle... (ej: chaqueta de cuero negra con tachuelas)"
            value={idea}
            onChange={(e) => setIdea(e.target.value)}
          />
        </section>
        <section className="st-section">
          <span className="st-section__title">Prompter</span>
          <textarea
            className="st-textarea"
            placeholder="Prompt tecnico..."
            value={prompter}
            onChange={(e) => setPrompter(e.target.value)}
          />
        </section>

        {/* Bottom bar */}
        <div className="st-bottom-bar">
          <div className="st-select-group">
            <span className="st-select-group__label">Motor</span>
            <select className="st-select" value={model} onChange={(e) => setModel(e.target.value)}>
              {MODELS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
          </div>
          <div className="st-select-group">
            <span className="st-select-group__label">Ratio</span>
            <select className="st-select" value={ratio} onChange={(e) => setRatio(e.target.value)}>
              {RATIOS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div className="st-select-group">
            <span className="st-select-group__label">Resolucion</span>
            <select className="st-select" value={resolution} onChange={(e) => setResolution(e.target.value)}>
              {RESOLUTIONS.map((r) => <option key={r} value={r}>{r}px</option>)}
            </select>
          </div>
          <div className="st-select-group">
            <span className="st-select-group__label">Semilla</span>
            <input className="st-seed-input" placeholder="random" value={seed} onChange={(e) => setSeed(e.target.value)} />
          </div>
          <span className="st-credits">Creditos est.: --</span>
        </div>

        {errorMsg && <div className="st-error">{errorMsg}</div>}
      </div>

      {/* RIGHT */}
      <div className="st-panel st-panel--right">
        <h2 className="st-panel__title">Resultado</h2>
        <div className="st-result">
          <div className={`st-result__image ${result ? "st-result__image--loaded" : ""}`}>
            {result ? (
              <img src={getResultSrc()} alt="Resultado" />
            ) : (
              <div className="st-results-empty">
                {status === "generating" ? <span className="st-spinner" /> : <p>Describe o carga referencia y pulsa Generar</p>}
              </div>
            )}
          </div>
          {result && (
            <div className="st-result__footer">
              <span className="st-result__label">{CATEGORIES.find((c) => c.id === category)?.label}</span>
              <div className="st-result__actions">
                <button className="st-btn-secondary">Guardar</button>
                <button className="st-btn-download">&#x2B73;</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {modalSrc && <ImageModal src={modalSrc} label="Detalle" onClose={() => setModalSrc(null)} />}
    </div>
  );
}
